import { Injectable, Injector } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthenticationService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

export interface NormalizedHttpError {
    message: string;
    status: number;
    success: 'Success' | 'Failed';
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    constructor(
        private authenticationService: AuthenticationService,
        private injector: Injector
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {

        return next.handle(request).pipe(
            catchError(err => {
                const status =
                    typeof err?.status === 'number'
                        ? err.status
                        : 0;

                const resolvedMessage =
                    this.extractMessage(err?.error) ||
                    this.getFallbackMessage(status);

                const normalizedError: NormalizedHttpError = {
                    message: resolvedMessage,
                    status,
                    success: 'Failed'
                };

                // Admin Panel ka existing logout behaviour.
                if (status === 401) {
                    this.authenticationService.logout();
                }

                const title =
                    `${this.getStatusTitle(status)} ` +
                    `(Error Code: ${status})`;

                this.injector
                    .get(ToastService)
                    .showToaster(
                        title,
                        resolvedMessage,
                        'error'
                    );

                return throwError(
                    () => normalizedError
                );
            })
        );
    }

    private extractMessage(
        body: unknown
    ): string | undefined {

        // Plain string API response.
        if (
            typeof body === 'string' &&
            body.trim()
        ) {
            return body.trim();
        }

        if (
            !body ||
            typeof body !== 'object'
        ) {
            return undefined;
        }

        const message = (
            body as { message?: unknown }
        ).message;

        // API response: { message: 'Customer already exists' }
        if (
            typeof message === 'string' &&
            message.trim()
        ) {
            return message.trim();
        }

        // API response: { message: ['Name is required', 'Email is invalid'] }
        if (Array.isArray(message)) {
            const messages = message
                .map(item => String(item).trim())
                .filter(Boolean);

            return messages.length
                ? messages.join(', ')
                : undefined;
        }

        return undefined;
    }

    private getStatusTitle(
        status: number
    ): string {

        if (status === 0) { return 'Connection Error'; }

        if (status === 400) { return 'Request Failed'; }

        if (status === 401) { return 'Unauthorized'; }

        if (status === 403) { return 'Forbidden'; }

        if (status === 404) { return 'Not Found'; }

        if (status === 409) { return 'Conflict'; }

        if (status === 422) { return 'Validation Failed'; }

        if (status === 429) { return 'Too Many Requests'; }

        if (status >= 500) { return 'Server Error'; }

        return 'Failed';
    }

    private getFallbackMessage(
        status: number
    ): string {

        if (status === 0) {
            return 'Unable to connect to the server. Please check your internet connection.';
        }

        if (status === 400) {
            return 'Please check the submitted information.';
        }

        if (status === 401) {
            return 'Your session has expired. Please login again.';
        }

        if (status === 403) {
            return 'You do not have permission to perform this action.';
        }

        if (status === 404) {
            return 'Requested information was not found.';
        }

        if (status === 409) {
            return 'The requested information conflicts with an existing record.';
        }

        if (status === 422) {
            return 'Some submitted information is invalid.';
        }

        if (status === 429) {
            return 'Too many requests. Please try again later.';
        }

        if (status >= 500) {
            return 'Server error occurred. Please try again later.';
        }

        return 'Something went wrong. Please try again.';
    }
}